import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, Card, CardContent, CircularProgress, Alert } from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import api from '../api';

export default function ScreeningInvite() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payLoading, setPayLoading] = useState(false);

  useEffect(() => {
    if (!orderId) {
      setError('Missing order ID');
      setLoading(false);
      return;
    }
    const fetchInvite = async () => {
      try {
        const res = await api.get(`/api/billing/screeningOrders/${orderId}/invite`);
        setInvite(res.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.error || 'Could not load screening invite');
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [orderId]);

  const handlePay = async () => {
    if (!orderId) return;
    setPayLoading(true);
    try {
      const res = await api.post(`/api/billing/screeningOrders/${orderId}/createCheckoutSession`);
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setError('No checkout URL returned');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start checkout');
    } finally {
      setPayLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invite) {
    return (
      <Box maxWidth={480} mx="auto" mt={4} px={2}>
        <Alert severity="error">{error || 'Invalid invite'}</Alert>
      </Box>
    );
  }

  const amount = (invite.priceCents / 100).toFixed(2);
  const currency = (invite.currency || 'CAD').toUpperCase();

  return (
    <Box maxWidth={480} mx="auto" mt={4} px={2}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Tenant screening
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            You’ve been invited to complete a background screening for a rental application.
          </Typography>
          <Typography variant="h5" sx={{ mt: 2 }}>
            {currency} {amount}
          </Typography>
          {invite.sku && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Package: {invite.sku}
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={<PaymentIcon />}
            onClick={handlePay}
            disabled={payLoading}
            fullWidth
            sx={{ mt: 3 }}
          >
            {payLoading ? 'Redirecting…' : 'Pay with Stripe'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
